import { CodeBlock } from '../components/CodeBlock';
import { ApiKeyDisplay } from '../components/ApiKeyDisplay';
import type { License } from '../api/portalApi';
import styles from './GettingStarted.module.css';

interface GettingStartedProps {
  licenses: License[];
}

const PLUGIN_INSTALL: Record<string, { pkg: string; provider: string; import: string }> = {
  'message-center': {
    pkg: '@chikchak/message-center',
    provider: 'MessageCenterProvider',
    import: "import { MessageCenterProvider } from '@chikchak/message-center';",
  },
  'admin-center': {
    pkg: '@chikchak/admin-center',
    provider: 'AdminCenterProvider',
    import: "import { AdminCenterProvider } from '@chikchak/admin-center';",
  },
  'debug-cloud': {
    pkg: '@chikchak/debug-cloud',
    provider: 'DebugCloudProvider',
    import: "import { DebugCloudProvider } from '@chikchak/debug-cloud';",
  },
};

export function GettingStarted({ licenses }: GettingStartedProps) {
  const activeLicenses = licenses.filter((l) => l.isActive);

  return (
    <div>
      <h1 className={styles.title}>Getting Started</h1>
      <p className={styles.subtitle}>Follow these steps to integrate ChikChak plugins into your app.</p>

      {activeLicenses.map((lic) => {
        const plugin = lic.plugins[0] ?? '';
        const info = PLUGIN_INSTALL[plugin];

        return (
          <div key={lic.apiKey} className={styles.pluginSection}>
            <h2 className={styles.pluginTitle}>{plugin}</h2>

            <div className={styles.step}>
              <div className={styles.stepNum}>1</div>
              <div>
                <h3>Your API Key</h3>
                <ApiKeyDisplay apiKey={lic.apiKey} />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNum}>2</div>
              <div>
                <h3>Install the package</h3>
                <CodeBlock language="bash" code={`npm install ${info?.pkg ?? plugin}`} />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNum}>3</div>
              <div>
                <h3>Add the provider</h3>
                <p className={styles.stepDesc}>Wrap your app (or a section of it) with the provider:</p>
                <CodeBlock
                  language="tsx"
                  code={`${info?.import ?? `import { Provider } from '${info?.pkg ?? plugin}';`}
import { createCloudAdapter } from '${info?.pkg ?? plugin}/adapters/cloud';

const adapters = createCloudAdapter({
  apiKey: '${lic.apiKey}',
});

function App() {
  return (
    <${info?.provider ?? 'Provider'}
      config={{
        siteId: 'my-site',
        ...adapters,
      }}
    >
      {/* Your app content */}
    </${info?.provider ?? 'Provider'}>
  );
}`}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNum}>4</div>
              <div>
                <h3>You're live!</h3>
                <p className={styles.stepDesc}>
                  Start your dev server and the plugin will connect to ChikChak Cloud automatically.
                  Your first domain will be auto-registered.
                </p>
              </div>
            </div>
          </div>
        );
      })}

      {activeLicenses.length === 0 && (
        <div className={styles.empty}>
          <p>No active licenses found. Get a license first to see install instructions.</p>
        </div>
      )}
    </div>
  );
}
